// src/theme/ThemeContext.tsx
import React, {createContext, useContext, type ReactNode, useState, useEffect, useMemo, useCallback} from 'react';
import configData from '../Config/config';
import type { LayoutItem, Config } from '../Config/config';
import { SystemModulesProvider } from './SystemModulesContext';
import { MediaService } from '../Services/MediaService';
import { isConfigLocalStorageEnabled } from '../Config/devMode';

interface ThemeContextType {
    config: Config;
    actions: {
        toggleDark: () => void;
        updateConfig: (newConfig: Partial<Config>) => void;
    };
    resolveComponents: (items: LayoutItem[]) => LayoutItem[]; // filtrage activations + fallback
}

const ThemeContext = createContext<ThemeContextType>({
    config: configData,
    actions: {toggleDark: () => {}, updateConfig: () => {}},
    resolveComponents: (items: LayoutItem[]) => items
});



export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    // Charger la sauvegarde depuis localStorage au démarrage
    const loadSavedConfig = (): Config => {
        if (!isConfigLocalStorageEnabled()) {
            console.log('🔒 Dev mode désactivé: chargement localStorage ignoré');
            return configData;
        }
        try {
            const savedConfig = localStorage.getItem('gandc_config_backup');
            if (savedConfig) {
                console.log('📁 Configuration sauvegardée trouvée, chargement...');
                return { ...configData, ...JSON.parse(savedConfig) };
            }
        } catch (error) {
            console.warn('⚠️ Erreur lors du chargement de la sauvegarde:', error);
        }
        console.log('📄 Utilisation de la configuration par défaut');
        return configData;
    };

    const [isDark, setIsDark] = useState(configData.isDark);
    const [runtimeConfig, setRuntimeConfig] = useState<Config>(() => {
        const config = loadSavedConfig();
        
        // Initialiser MediaService de manière synchrone dès le chargement
        if (config.api) {
            const mediaConfig = config.api.media || {
                lazyLoadThreshold: '200px',
                enableProgressiveLoading: true,
                apiTimeout: 5000,
                defaultQuality: 85
            };
            
            MediaService.init({
                baseUrl: config.api.baseUrl,
                readToken: config.api.readToken,
                mediaConfig,
                minQuality: mediaConfig.minQuality,
                maxQuality: mediaConfig.maxQuality
            });
        }
        
        return config;
    });

    // Réinitialiser MediaService si la config API change
    useEffect(() => {
        if (runtimeConfig.api) {
            const mediaConfig = runtimeConfig.api.media || {
                lazyLoadThreshold: '200px',
                enableProgressiveLoading: true,
                apiTimeout: 5000,
                defaultQuality: 85
            };
            
            MediaService.init({
                baseUrl: runtimeConfig.api.baseUrl,
                readToken: runtimeConfig.api.readToken,
                mediaConfig,
                minQuality: mediaConfig.minQuality,
                maxQuality: mediaConfig.maxQuality
            });
        }
    }, [runtimeConfig.api]);

    useEffect(() => {
        const mq = window.matchMedia("(prefers-color-scheme: dark)");

        if (runtimeConfig.darkModeEnabled) {
            setIsDark(mq.matches);
        }

        const listener = (evt: MediaQueryListEvent) => {
            if (runtimeConfig.darkModeEnabled) setIsDark(evt.matches);
        };

        mq.addEventListener("change", listener);
        return () => mq.removeEventListener("change", listener);
    }, [runtimeConfig.darkModeEnabled]);

    useEffect(() => {
        const root = document.documentElement;
        root.style.setProperty(
            '--primary-color',
            isDark ? runtimeConfig.darkPrimaryColor || runtimeConfig.primaryColor : runtimeConfig.primaryColor
        );
        root.style.setProperty(
            '--secondary-color',
            isDark ? runtimeConfig.darkSecondaryColor || runtimeConfig.secondaryColor : runtimeConfig.secondaryColor
        );
        root.style.setProperty(
            '--accentuated-color',
            isDark ? runtimeConfig.darkAccentuatedColor || runtimeConfig.accentuatedColor : runtimeConfig.accentuatedColor
        );
        root.style.setProperty('--background-color', isDark ? '#222' : '#F5F5F5');
        root.style.setProperty('--text-color', isDark ? '#F5F5F5' : '#000');
        root.style.setProperty('--font-family', runtimeConfig.fontFamily);
    }, [isDark, runtimeConfig]);


    // Sauvegarde automatique dans localStorage
    const saveToLocalStorage = useCallback((config: Config) => {
        if (!isConfigLocalStorageEnabled(config)) {
            return;
        }
        try {
            localStorage.setItem('gandc_config_backup', JSON.stringify(config));
            console.log('💾 Configuration sauvegardée automatiquement');
        } catch (error) {
            console.warn('⚠️ Erreur lors de la sauvegarde automatique:', error);
        }
    }, []);

    const actions = useMemo(() => ({
        toggleDark: () => setIsDark(prev => !prev),
        updateConfig: (newConfig: Partial<Config>) => {
            setRuntimeConfig(prev => {
                const updated = { ...prev, ...newConfig };
                // Sauvegarder automatiquement après chaque modification
                setTimeout(() => saveToLocalStorage(updated), 100);
                return updated;
            });
        }
    }), [saveToLocalStorage]);

    // Mettre à jour le configData.isDark dynamiquement
    const config = useMemo(() => ({ ...runtimeConfig, isDark }), [runtimeConfig, isDark]);

    const resolveComponents = useCallback((items: LayoutItem[]) => {
        // 1) garder uniquement enabled et modules activés
        const enabled = items.filter(it => (it.enabled ?? true) && (runtimeConfig.modules?.[it.component] ?? true));
        // 2) fallback: si un composant manque (module désactivé), l'auto-placement opérera plus tard;
        // ici on peut ré-ordonner pour combler les trous simplement en gardant l'ordre restant
        return enabled;
    }, [runtimeConfig.modules]);

    // Utiliser useMemo pour créer une nouvelle référence quand config change
    const contextValue = useMemo(() => ({
        config,
        actions,
        resolveComponents
    }), [config, actions, resolveComponents]);

    return (
        <ThemeContext.Provider value={contextValue}>
            <SystemModulesProvider
                apiUrl={runtimeConfig.api.baseUrl}
                apiEnabled={runtimeConfig.api.enableSync}
            >
                {children}
            </SystemModulesProvider>
        </ThemeContext.Provider>
    );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useThemeContext = () => useContext(ThemeContext);
